/*
 * Toggle.js
 *
 * Accessible region toggle.
 * Toggles a region's visibility with a button by manipulating ARIA states.
 * Automatically hides other regions managed by buttons in a matched set of elements.
 *
 * @link https://github.com/croxton/toggle.js
 * @author Mark Croxton
 * @version 1.0.0
 * @copyright Mark Croxton 2020
 */

import PubSub from "pubsub-js";

class Toggle {

    /*
     * Setup buttons and panels
     */
    constructor(el, init= false, linked = true) {

        let self = this;
        this.buttons = document.querySelectorAll(el);
        this.init = init;
        this.linked = linked;
        this.delayFocus = null;

        // set initial toggle state (responsive)
        if (this.init) {
            self.resetToggleState();
        }

        for (let i=0; i < this.buttons.length; i++) {
            this.buttons[i].addEventListener("click", function(e) {
                e.preventDefault();
                self.toggle(e.currentTarget);
            });

            // if there is a 'close' button in the panel associated with this button,
            // allow it to close the panel (if it is open), and return focus to the button
            let btnPanel = document.querySelector('#' + this.buttons[i].getAttribute('aria-controls'));
            if (btnPanel != null) {
                let closeBtn = btnPanel.querySelector('[data-close]');
                if (closeBtn != null) {
                    closeBtn.addEventListener("click", () => {
                        if (self.buttons[i].getAttribute('aria-expanded') === 'true') {
                            self.buttons[i].click(); // triggers doToggle()
                            self.buttons[i].focus();
                        }
                    });
                }
            }
        }

        // allow user to press the ESCAPE key to close any open regions
        window.addEventListener("keyup", function(e){
            let key = e.key || e.keyCode;
            if (key === 'Escape' || key === 'Esc' || key === 27) {
                for (let i=0; i < self.buttons.length; i++) {
                    if (self.buttons[i].getAttribute('aria-expanded') === 'true') {
                        self.buttons[i].click(); // triggers doToggle()
                        self.buttons[i].focus(); // will return focus to last button in sequence
                    }
                }
            }

        });

        // subscribe to window resize done event
        PubSub.subscribe('resizeDone', function (msg, data) {
            self.resetToggleState();
        });
    }

    resetToggleState() {

        for (let i=0; i < this.buttons.length; i++) {

            let btn = this.buttons[i];
            let btnPanel = document.querySelector('#' + btn.getAttribute('aria-controls'));

            if (btnPanel != null && ! btn.hasAttribute('data-toggle-once')) {

                // remove aria state and calculate 'natural' display property
                btnPanel.setAttribute('aria-hidden', null);

                if (window.getComputedStyle(btnPanel).display === 'none' || window.getComputedStyle(btnPanel).visibility === 'hidden') {
                    this.closePanel(btn, btnPanel);
                } else {
                    this.openPanel(btn, btnPanel, false);
                }
            }

        }
    }

    /*
     * Event handler for region toggle
     */
    toggle(targetBtn, state, focus) {

        state = state ?? (targetBtn.getAttribute('aria-expanded') === 'false');
        focus = focus ?? true;

        let targetPanel = document.querySelector('#' + targetBtn.getAttribute('aria-controls'));

        // update classes
        if (targetPanel) {

            if (state === true) {

                this.openPanel(targetBtn, targetPanel, focus);

                // when opening a region, close other open buttons in the matched set
                if (this.linked) {

                    for (let i = 0; i < this.buttons.length; i++) {

                        let btn = this.buttons[i];

                        if (btn !== targetBtn) {
                            let btnPanel = document.querySelector('#' + btn.getAttribute('aria-controls'));
                            this.closePanel(btn, btnPanel);
                        }
                    }
                }

            } else {
                // closing an open panel
                this.closePanel(targetBtn, targetPanel);
            }
        }
    }

    closePanel(btn, panel) {

        // update state
        btn.setAttribute('aria-expanded', 'false');
        panel.setAttribute('aria-hidden', 'true');

        // reset tabindexes
        let tabIndexes = panel.querySelectorAll('[tabindex]');
        for (let i=0; i < tabIndexes.length; i++) {
            tabIndexes[i].setAttribute("tabindex", "-1");
        }

        // self active class
        if (btn.dataset.active) {
            btn.classList.remove(btn.dataset.active);
        }

        // parent active class
        if (btn.dataset.activeParent) {
            btn.parentNode.classList.remove(btn.dataset.activeParent);
        }

        // root active class
        if (btn.dataset.activeRoot) {
            document.querySelector('html').classList.remove(btn.dataset.activeRoot);
        }

        // target active class
        if (btn.dataset.activeTarget && btn.dataset.activeTargetId) {
            let target = document.getElementById(btn.dataset.activeTargetId);
            if (target) {
                target.classList.remove(btn.dataset.activeTarget);
            }
        }
    }

    openPanel(btn, panel, focus) {

        // opening a panel
        btn.setAttribute('aria-expanded', 'true');
        panel.setAttribute('aria-hidden', 'false');

        // toggle any tabindexes in the region...
        let tabIndexes = panel.querySelectorAll('[tabindex]');
        for (let i=0; i < tabIndexes.length; i++) {
            tabIndexes[i].setAttribute("tabindex", "0");
        }

        // ..but tabindex for links in nested regions should remain inactive
        let tabIndexesNested = panel.querySelectorAll('[aria-hidden="true"] [tabindex="0"]');
        for (let i=0; i < tabIndexesNested.length; i++) {
            tabIndexesNested[i].setAttribute("tabindex", "-1");
        }

        // activate self
        if (btn.dataset.active) {
            btn.classList.add(btn.dataset.active);
        }

        // activate parent
        if (btn.dataset.activeParent) {
            btn.parentNode.classList.add(btn.dataset.activeParent);
        }

        // activate root
        if (btn.dataset.activeRoot) {
            document.querySelector('html').classList.add(btn.dataset.activeRoot);
        }

        // target active class
        if (btn.dataset.activeTarget && btn.dataset.activeTargetId) {
            let target = document.getElementById(btn.dataset.activeTargetId);
            if (target) {
                document.getElementById(btn.dataset.activeTargetId).classList.add(btn.dataset.activeTarget);
            }
        }

        // focus first element in the newly opened panel
        if (focus) {
            if (btn.dataset.focus) {
                let focusable = panel.querySelector(btn.dataset.focus);
                clearTimeout(self.delayFocus);
                if (focusable) {
                    self.delayFocus = setTimeout(function() {
                        focusable.focus();
                    }, 300);
                }

            } else {
                let focusable = panel.querySelectorAll('button:not([data-close]), [href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])');
                if (focusable.length) {
                    let firstFocusable = focusable[0];
                    clearTimeout(self.delayFocus);
                    self.delayFocus = setTimeout(function() {
                        firstFocusable.focus();
                    }, 300);
                }
            }

        }
    }

}

export default Toggle;